import Reflux from 'reflux';
import { fromJS, Map } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import ActivityAuditListActions from 'flux/actions/ActivityAuditListActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';

const itemColumn = 'itemColumn';
const itemField = 'itemField';
const softDeletedItemRows = 'softDeletedItemRows';
const softDeletedColumns = 'softDeletedColumns';
const methodName = 'methodName';

/**
 * ActivityAuditListStore
 *
 * Keeps state for activity audit list messages
 *
 */
export default Reflux.createStore({
  listenables: [ActivityAuditListActions, RealTimeActions],

  mixins: [BaseMixin],

  init() {
    this.listenTo(CuesheetStore, this.onCuesheetStoreChange);

    this.setState(
      Map({
        list: null,
        currentPage: 1,
        visible: false,
        expanded: false,
        loading: false,
      }),
    );
  },

  /**
   * We listen for changes on the cuesheet, because we need to merge current data from the cuesheet w/ the activity audit feed
   */
  onCuesheetStoreChange(cuesheetState) {
    let rowMap = cuesheetState.get('rowMap'),
      rows = cuesheetState.get('rows'),
      nextShowId = cuesheetState.get('showId');

    /**
     * If the show is different in the cuesheet, we need
     */
    if (this._showId && nextShowId && nextShowId !== this._showId) {
      this.setInState('list', null);
      return ActivityAuditListActions.populateForShow(
        cuesheetState.get('teamId'),
        cuesheetState.get('eventId'),
        nextShowId,
        1,
      );
    }

    /**
     * If there is no rowMap, we have nothing to do
     */
    if (!rowMap) return;

    this.setInState('show', cuesheetState.get('show'));

    /**
     * Set the full columns so we can use them to decorate audit messages
     */
    this._buildColumnAttributesMap(cuesheetState.get('rawColumns'));

    /**
     * Build a map of items attributes so we can use them to decorate audit messages
     */
    this._buildItemAttributesMap(rowMap, rows);
  },

  _buildColumnAttributesMap(columns) {
    this._columnAttributesMap = Map(
      columns.map((col) => [col.get('_id'), col]),
    );
  },

  _getColumnTitle(columnId) {
    return this._columnAttributesMap.getIn([columnId, 'title']);
  },

  /**
   * Builds an item/row attribute map (rowId => {attributes...})
   *
   *
   */
  // eslint-disable-next-line no-unused-vars
  _buildItemAttributesMap(rowMap, rows) {
    let itemSetId = rowMap.keySeq().first();

    this._itemAttributesMap = Map().withMutations((itemNumberMap) => {
      rowMap.getIn([itemSetId, 'rows']).forEach((row, rowId) => {
        let item = row.get('item'),
          numberAdjusted = item.get('numberAdjusted'),
          number = item.get('number'),
          title = item.get('title');

        itemNumberMap.setIn([rowId, 'numberAdjusted'], numberAdjusted);
        itemNumberMap.setIn([rowId, 'number'], number);
        itemNumberMap.setIn([rowId, 'title'], title);
      });
    });

    /**
     * If we get an updated attribute map, we need to update the audit messages and notify listeners
     */
    this._decorateAuditMessagesAndTrigger();
  },

  onToggleVisibility(display) {
    let next = this.getState().update('visible', (v) => {
      return typeof display === 'boolean' ? display : !v;
    });

    this.setStateAndTrigger(next);

    /**
     * If we are now visible, we should refresh..
     */
    if (next.get('visible')) {
      this._refresh();
    }
  },

  onRefresh() {
    this._refresh();
  },

  /**
   * Refresh the activity feed with the current parameters
   */
  _refresh() {
    this._refreshPage(this.getInState('currentPage'));
  },

  /**
   * [BEGIN] Show population hooks
   */
  onPopulateForShow(teamId, eventId, showId, pageNum) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    delete this._itemId;
    delete this._columnId;

    this.setState(
      next
        .set('currentPage', pageNum)
        .delete('selectedItem')
        .delete('selectedColumn')
        .delete('selectedField')
        .delete('listType'),
    );

    this.setInStateAndTrigger('loading', true);
  },

  onPopulateForShowCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END] Show population hooks
   **/

  /**
   * [BEGIN] Item cell population hooks
   */
  onPopulateForItemCell(teamId, eventId, showId, itemId, columnId, pageNum) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    this._columnId = columnId;
    this._itemId = itemId;

    let itemAttrs = this._itemAttributesMap.get(itemId);

    this.setState(
      next
        .set('visible', true)
        .set('currentPage', pageNum)
        .set('selectedItem', itemAttrs)
        .set('listType', itemColumn)
        .delete('selectedField')
        .set('selectedColumn', this._getColumnTitle(columnId)),
    );
    this.setInStateAndTrigger('loading', true);
  },

  onPopulateForItemCellCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END Item cell population hooks
   */

  /**
   * [BEGIN] Item field population hooks
   */
  onPopulateForItemField(teamId, eventId, showId, itemId, fieldName, pageNum) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    this._fieldName = fieldName;
    this._itemId = itemId;

    const itemAttrs = this._itemAttributesMap.get(itemId);

    next = next
      .set('visible', true)
      .set('currentPage', pageNum)
      .set('selectedItem', itemAttrs)
      .set('selectedField', fieldName)
      .delete('selectedColumn')
      .set('listType', itemField)
      .set('loading', true);

    this.setStateAndTrigger(next);
  },

  onPopulateForItemFieldCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END Item field population hooks
   */

  /**
   * [BEGIN] Soft deleted rows population hooks
   */
  onPopulateForSoftDeletedItemRows(
    teamId,
    eventId,
    showId,
    itemSetId,
    pageNum,
  ) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    this._itemSetId = itemSetId;
    delete this._fieldName;
    delete this._itemId;

    this.setState(
      next
        .set('visible', true)
        .set('currentPage', pageNum)
        .set('listType', softDeletedItemRows)
        .delete('selectedItem')
        .delete('selectedColumn')
        .delete('selectedField'),
    );

    this.setInStateAndTrigger('loading', true);
  },

  onPopulateForSoftDeletedItemRowsCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END Soft deleted rows population hooks
   */

  /**
   * [BEGIN] Soft deleted columns population hooks
   */
  onPopulateForSoftDeletedColumns(
    teamId,
    eventId,
    showId,
    itemSetListId,
    pageNum,
  ) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    this._itemSetListId = itemSetListId;
    delete this._fieldName;
    delete this._itemId;

    this.setState(
      next
        .set('visible', true)
        .set('currentPage', pageNum)
        .set('listType', softDeletedColumns)
        .delete('selectedItem')
        .delete('selectedColumn')
        .delete('selectedField'),
    );

    this.setInStateAndTrigger('loading', true);
  },

  onPopulateForSoftDeletedColumnsCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END Soft deleted columns population hooks
   */

  /**
   * [BEGIN] Method name population hooks
   */
  onPopulateForMethodName(teamId, eventId, showId, method, pageNum) {
    let next = this.getState();

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;
    this._methodName = method;
    delete this._fieldName;
    delete this._itemId;

    this.setState(
      next
        .set('visible', true)
        .set('currentPage', pageNum)
        .set('listType', methodName)
        .delete('selectedItem')
        .delete('selectedColumn')
        .delete('selectedField'),
    );

    this.setInStateAndTrigger('loading', true);
  },

  onPopulateForMethodNameCompleted(activityAuditMessages) {
    this._populateCompleted(activityAuditMessages);
  },

  /**
   * [END Method name population hooks
   */

  onChangePage(pageNum) {
    this._refreshPage(pageNum);
  },

  _refreshPage(pageNum) {
    let listType = this.getInState('listType');

    if (listType === itemColumn) {
      ActivityAuditListActions.populateForItemCell(
        this._teamId,
        this._eventId,
        this._showId,
        this._itemId,
        this._columnId,
        pageNum,
      );
    } else if (listType === itemField) {
      ActivityAuditListActions.populateForItemField(
        this._teamId,
        this._eventId,
        this._showId,
        this._itemId,
        this._fieldName,
        pageNum,
      );
    } else if (listType === softDeletedItemRows) {
      ActivityAuditListActions.populateForSoftDeletedItemRows(
        this._teamId,
        this._eventId,
        this._showId,
        this._itemSetId,
        pageNum,
      );
    } else if (listType === softDeletedColumns) {
      ActivityAuditListActions.populateForSoftDeletedColumns(
        this._teamId,
        this._eventId,
        this._showId,
        this._itemSetListId,
        pageNum,
      );
    } else if (listType === methodName) {
      ActivityAuditListActions.populateForMethodName(
        this._teamId,
        this._eventId,
        this._showId,
        this._methodName,
        pageNum,
      );
    } else {
      ActivityAuditListActions.populateForShow(
        this._teamId,
        this._eventId,
        this._showId,
        pageNum,
      );
    }
  },

  /**
   * Helper for setting activity audit message state
   */
  _populateCompleted(activityAuditMessages) {
    let next = this.getState()
      .set('loading', false)
      .set('list', fromJS(activityAuditMessages));

    this.setState(next);

    this._decorateAuditMessagesAndTrigger();
  },

  /*
   * This will remove all duplicates in the users list and return trimmed user list
   */
  _getFilterUserList(state) {
    state = state || this.getState();

    // TODO: Get list of users in this show instead of users that are in activity feed...
    let items = state.getIn(['list', 'items']);

    if (!items || !items.size) return null;

    let users = items.map((item) => {
      return (
        item.getIn(['user', 'firstName']) +
        ' ' +
        item.getIn(['user', 'lastName'])
      );
    });

    let filteredUsers = users.reduce((a, b) => {
      if (a.indexOf(b) < 0) a.push(b);
      return a;
    }, []);

    return filteredUsers;
  },

  /**
   * Decorate the real time feed w/ add'l data
   */
  _decorateAuditMessagesAndTrigger() {
    if (!this._itemAttributesMap || !this.getState().hasIn(['list', 'items']))
      return;

    let next = this.getState().updateIn(['list', 'items'], (items) => {
      return items.map((item) => {
        let attrs = null;

        if (item.hasIn(['payload', 'rowId'])) {
          attrs = this._itemAttributesMap.get(item.getIn(['payload', 'rowId']));
        } else if (
          item.hasIn(['payload', 'rowIds']) &&
          item.getIn(['payload', 'rowIds']).size === 1
        ) {
          attrs = this._itemAttributesMap.get(
            item.getIn(['payload', 'rowIds', 0]),
          );
        } else if (item.has('rowIds') && item.get('rowIds').size === 1) {
          attrs = this._itemAttributesMap.get(item.getIn(['rowIds', 0]));
        } else if (item.hasIn(['payload', 'globalElement', 'row'])) {
          attrs = this._itemAttributesMap.get(
            item.getIn(['payload', 'globalElement', 'row']),
          );
        } else if (item.hasIn(['params', 'rowId'])) {
          attrs = this._itemAttributesMap.get(item.getIn(['params', 'rowId']));
        }

        let number = '---';
        let title = '---';

        if (attrs) {
          title = attrs.get('title');

          if (this.getInState(['show', 'autoCountCues'])) {
            number =
              typeof attrs.get('numberAdjusted') === 'number'
                ? attrs.get('numberAdjusted')
                : '---';
          } else {
            number = attrs.get('number');
          }
        }

        item = item.set('itemNumber', number).set('itemTitle', title);

        if (item.hasIn(['payload', 'columnId'])) {
          item = item.set(
            'columnTitle',
            this._getColumnTitle(item.getIn(['payload', 'columnId'])),
          );
        }

        return item;
      });
    });

    /**
     * Only show one deleted row item per unique row
     */
    if (next.get('listType') === softDeletedItemRows)
      next = this._filterDeletedRowItems(next);
    if (next.get('listType') === softDeletedColumns)
      next = this._filterDeletedColumns(next);

    this.setStateAndTrigger(next);
  },

  /**
   * Only show one deleted row per unique row
   */
  _filterDeletedRowItems(next) {
    return next.updateIn(['list', 'items'], (items) => {
      let rowIds = {};

      return items.filter((item) => {
        let rowId = item.getIn(['rowIds', 0]);

        if (rowIds[rowId]) return false;

        rowIds[rowId] = true;

        return true;
      });
    });
  },

  /**
   * Only show one deleted column per unique column
   */
  _filterDeletedColumns(next) {
    return next.updateIn(['list', 'items'], (items) => {
      let columnIds = {};

      return items.filter((item) => {
        let columnId = item.get('columnId');

        if (columnIds[columnId]) return false;

        columnIds[columnId] = true;

        return true;
      });
    });
  },
});
